import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import schemaService from '../services/schemaService';

// Get all schemas
export const getSchemas = createAsyncThunk(
  'schemas/getSchemas',
  async (filters = {}, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('schemasSlice: Calling schemaService.getAll() with filters:', filters);
      }
      const schemas = await schemaService.getAll(filters);
      if (process.env.NODE_ENV === 'development') {
        console.log('schemasSlice: Received schemas from service:', schemas);
      }
      return schemas;
    } catch (error) {
      console.error('schemasSlice: Error fetching schemas:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Get schema by ID
export const getSchemaById = createAsyncThunk(
  'schemas/getSchemaById',
  async (id, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('schemasSlice: Calling schemaService.getById() with ID:', id);
      }
      const schema = await schemaService.getById(id);
      if (process.env.NODE_ENV === 'development') {
        console.log('schemasSlice: Received schema from service:', schema);
      }
      return schema;
    } catch (error) {
      console.error('schemasSlice: Error fetching schema by ID:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Create new schema
export const createSchema = createAsyncThunk(
  'schemas/createSchema',
  async (schemaData, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('schemasSlice: Calling schemaService.create() with data:', schemaData);
      }
      const schema = await schemaService.create(schemaData);
      if (process.env.NODE_ENV === 'development') {
        console.log('schemasSlice: Created schema:', schema);
      }
      return schema;
    } catch (error) {
      console.error('schemasSlice: Error creating schema:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Update existing schema
export const updateSchema = createAsyncThunk(
  'schemas/updateSchema',
  async ({ id, schemaData }, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('schemasSlice: Calling schemaService.update() with ID:', id, 'and data:', schemaData);
      }
      const schema = await schemaService.update(id, schemaData);
      if (process.env.NODE_ENV === 'development') {
        console.log('schemasSlice: Updated schema:', schema);
      }
      return schema;
    } catch (error) {
      console.error('schemasSlice: Error updating schema:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Delete schema
export const deleteSchema = createAsyncThunk(
  'schemas/deleteSchema',
  async (id, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('schemasSlice: Calling schemaService.delete() with ID:', id);
      }
      await schemaService.delete(id);
      if (process.env.NODE_ENV === 'development') {
        console.log('schemasSlice: Deleted schema with ID:', id);
      }
      return id;
    } catch (error) {
      console.error('schemasSlice: Error deleting schema:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// NEW: Validate data against schema
export const validateSchemaData = createAsyncThunk(
  'schemas/validateData',
  async ({ schemaId, data, validationLevel }, { rejectWithValue }) => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('schemasSlice: Calling schemaService.validateData() with schema ID:', schemaId);
        console.log('schemasSlice: Validation level:', validationLevel);
      }
      const result = await schemaService.validateData(schemaId, data, validationLevel);
      if (process.env.NODE_ENV === 'development') {
        console.log('schemasSlice: Validation result:', result);
      }
      return result;
    } catch (error) {
      console.error('schemasSlice: Error validating schema data:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const schemasSlice = createSlice({
  name: 'schemas',
  initialState: {
    schemas: [],
    currentSchema: null,
    loading: false,
    error: null,
    success: false,
    totalCount: 0,
    currentPage: 1,
    pageSize: 50,
    // NEW: Validation state
    validationResult: null,
    validationLoading: false
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    setCurrentSchema: (state, action) => {
      state.currentSchema = action.payload;
    },
    clearCurrentSchema: (state) => {
      state.currentSchema = null;
    },
    // NEW: Clear validation result
    clearValidationResult: (state) => {
      state.validationResult = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Get schemas
      .addCase(getSchemas.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSchemas.fulfilled, (state, action) => {
        state.loading = false;
        state.schemas = action.payload;
        state.error = null;
      })
      .addCase(getSchemas.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get schema by ID
      .addCase(getSchemaById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSchemaById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSchema = action.payload;
        state.error = null;
      })
      .addCase(getSchemaById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create schema
      .addCase(createSchema.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createSchema.fulfilled, (state, action) => {
        state.loading = false;
        state.schemas.push(action.payload);
        state.success = true;
        state.error = null;
      })
      .addCase(createSchema.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Update schema
      .addCase(updateSchema.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateSchema.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.schemas.findIndex(schema => schema.schema_id === action.payload.schema_id);
        if (index !== -1) {
          state.schemas[index] = action.payload;
        }
        state.currentSchema = action.payload;
        state.success = true;
        state.error = null;
      })
      .addCase(updateSchema.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // Delete schema
      .addCase(deleteSchema.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteSchema.fulfilled, (state, action) => {
        state.loading = false;
        state.schemas = state.schemas.filter(schema => schema.schema_id !== action.payload);
        state.success = true;
        state.error = null;
      })
      .addCase(deleteSchema.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })
      // NEW: Validate schema data
      .addCase(validateSchemaData.pending, (state) => {
        state.validationLoading = true;
        state.error = null;
        state.validationResult = null;
      })
      .addCase(validateSchemaData.fulfilled, (state, action) => {
        state.validationLoading = false;
        state.validationResult = action.payload;
      })
      .addCase(validateSchemaData.rejected, (state, action) => {
        state.validationLoading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, clearSuccess, setCurrentSchema, clearCurrentSchema, clearValidationResult } = schemasSlice.actions;

export default schemasSlice.reducer;
